import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchBillingInfo } from '../lib/api';
import { useLang } from '../lib/i18n';

// Persistent warning banner shown on every authenticated page whenever
// XyloTrust has been uninstalled in the customer's Freshdesk. Detects the
// state via BillingInfo.purge_scheduled_at — set by XyloTrust's GDPR
// soft-delete on uninstall, cleared on re-install. Shows a 30-day-purge
// countdown + reinstall CTA so the customer isn't blindsided when their
// data disappears.
export function UninstallBanner() {
  const { t } = useLang();
  const { data } = useQuery({
    queryKey: ['billing-info'],
    queryFn: fetchBillingInfo,
    staleTime: 60_000, // 1 min — the banner doesn't need to be real-time
  });

  if (!data || data.purge_scheduled_at == null) return null;

  const now = Math.floor(Date.now() / 1000);
  const secondsLeft = data.purge_scheduled_at - now;
  const daysLeft = Math.max(0, Math.ceil(secondsLeft / 86400));

  const body = daysLeft > 0
    ? t.uninstallBanner.bodyDays.replace('{days}', String(daysLeft))
    : t.uninstallBanner.bodyToday;

  return (
    <div
      className="bg-amber-50 border-b border-amber-200 px-6 py-3 text-sm text-amber-900"
      role="alert"
      aria-live="polite"
    >
      <div className="max-w-5xl mx-auto flex items-start gap-3">
        <span className="text-xl leading-none">⚠️</span>
        <div className="flex-1">
          <div className="font-semibold">{t.uninstallBanner.title}</div>
          <div className="mt-0.5">{body} {t.uninstallBanner.cta}</div>
        </div>
      </div>
    </div>
  );
}
